import { type ReactElement } from 'react';
import { layoutSizes } from '@/theme/layoutSizes';
import { s } from '@/theme/tokens';
import { Stack } from '@/ui';
import { Box, PageShell } from '@/ui/primitives';
import { EarlyAccessSection } from './landing/EarlyAccessSection';
import { ExecutionTablesCarouselSection } from './landing/ExecutionTablesCarouselSection';
import { FeaturePanelsSection } from './landing/FeaturePanelsSection';
import { FlowStepsSection } from './landing/FlowStepsSection';
import { featurePanels, landingSizes } from './landing/landingData';
import { LandingHeroSection } from './landing/LandingHeroSection';
import { QualityHighlightsCarouselSection } from './landing/QualityHighlightsCarouselSection';
import { TestingChangedSection } from './landing/TestingChangedSection';
import { useLandingViewModel } from './landing/useLandingViewModel';

export const LandingContent = (): ReactElement => {
  const viewModel = useLandingViewModel();
  const {
    headerWidth,
    panelsWidth,
    testingChangedWidth,
    carouselSectionWidth,
    registerInterestWidth,
  } = landingSizes;
  const chartHeight = 200;
  const staggerStepMs = 400;
  const staggerBaseMs = 220;
  const toggleButtonHeight = layoutSizes.sidebar.itemMinHeight;
  return (
    <Box
      className="landing-page"
      sx={{ backgroundColor: 'background.default', color: 'text.primary' }}
    >
      <PageShell maxWidth="xl" paddingX="lg" paddingY="xl" paddingTop="none" paddingBottom="none">
        <Stack
          gap="3xl"
          sx={(theme) => ({
            '--landing-stagger-step': `${staggerStepMs}ms`,
            '--landing-stagger-base': `${staggerBaseMs}ms`,
            '& > *': {
              opacity: viewModel.staggerReady ? 1 : 0,
              transform: viewModel.staggerReady ? 'translateY(0)' : `translateY(${s('lg')})`,
              transitionProperty: 'opacity, transform',
              transitionDuration: '3000ms',
              transitionTimingFunction: theme.app.motion.easing.standard,
            },
            '& > *:nth-of-type(1)': { transitionDelay: 'var(--landing-stagger-base)' },
            '& > *:nth-of-type(2)': {
              transitionDelay: 'calc(var(--landing-stagger-base) + var(--landing-stagger-step))',
            },
            '& > *:nth-of-type(3)': {
              transitionDelay:
                'calc(var(--landing-stagger-base) + (var(--landing-stagger-step) * 2))',
            },
            '& > *:nth-of-type(4)': {
              transitionDelay:
                'calc(var(--landing-stagger-base) + (var(--landing-stagger-step) * 3))',
            },
            '& > *:nth-of-type(5)': {
              transitionDelay:
                'calc(var(--landing-stagger-base) + (var(--landing-stagger-step) * 4))',
            },
            '& > *:nth-of-type(6)': {
              transitionDelay:
                'calc(var(--landing-stagger-base) + (var(--landing-stagger-step) * 5))',
            },
            '& > *:nth-of-type(7)': {
              transitionDelay:
                'calc(var(--landing-stagger-base) + (var(--landing-stagger-step) * 6))',
            },
            '& > *:nth-of-type(8)': {
              transitionDelay:
                'calc(var(--landing-stagger-base) + (var(--landing-stagger-step) * 7))',
            },
            '& > *:nth-of-type(9)': {
              transitionDelay:
                'calc(var(--landing-stagger-base) + (var(--landing-stagger-step) * 8))',
            },
          })}
        >
          <Stack gap="none">
            <LandingHeroSection headerWidth={headerWidth} />
          </Stack>
          <Box sx={{ px: s('md') }}>
            <Stack gap="none" sx={{ gap: `calc(${s('2xl')} + ${s('2xl')})` }}>
              <TestingChangedSection width={testingChangedWidth} />
              <FlowStepsSection width={panelsWidth} />
              <QualityHighlightsCarouselSection
                width={carouselSectionWidth}
                chartHeight={chartHeight}
                toggleButtonHeight={toggleButtonHeight}
                rangeTab={viewModel.chart.rangeTab}
                onRangeTabChange={viewModel.chart.setRangeTab}
                statusCards={viewModel.chart.statusCards}
                metricCardsForRange={viewModel.chart.metricCardsForRange}
                chartTab={viewModel.chart.chartTab}
                onChartTabChange={viewModel.chart.setChartTab}
                statusTrendChartData={viewModel.chart.statusTrendChartData}
                passRateChartData={viewModel.chart.passRateChartData}
                frequencyChartData={viewModel.chart.frequencyChartData}
                durationChartData={viewModel.chart.durationChartData}
                carouselIndex={viewModel.carousels.carouselIndex}
                direction={viewModel.carousels.carouselDirection}
                onPrev={viewModel.carousels.onPrevSlide}
                onNext={viewModel.carousels.onNextSlide}
                onSelect={viewModel.carousels.setCarouselIndex}
                slides={viewModel.carousels.carouselSlides}
              />
              <FeaturePanelsSection width={panelsWidth} panels={featurePanels} />
              <ExecutionTablesCarouselSection
                width={carouselSectionWidth}
                carouselIndex={viewModel.carousels.tablesCarouselIndex}
                direction={viewModel.carousels.tablesCarouselDirection}
                onPrev={viewModel.carousels.onPrevTableSlide}
                onNext={viewModel.carousels.onNextTableSlide}
                onSelect={viewModel.carousels.setTablesCarouselIndex}
                slides={viewModel.carousels.tablesCarouselSlides}
                suiteExecutionsQuery={viewModel.tableQueries.suiteExecutionsQuery}
                suitesQuery={viewModel.tableQueries.suitesQuery}
              />
            </Stack>
          </Box>
          <EarlyAccessSection
            firstName={viewModel.form.firstName}
            lastName={viewModel.form.lastName}
            companyName={viewModel.form.companyName}
            email={viewModel.form.email}
            role={viewModel.form.role}
            companySize={viewModel.form.companySize}
            onFirstNameChange={viewModel.form.onFirstNameChange}
            onLastNameChange={viewModel.form.onLastNameChange}
            onCompanyNameChange={viewModel.form.onCompanyNameChange}
            onEmailChange={viewModel.form.onEmailChange}
            onRoleChange={viewModel.form.onRoleChange}
            onCompanySizeChange={viewModel.form.onCompanySizeChange}
            onSubmit={viewModel.form.onSubmit}
            width={registerInterestWidth}
            isSubmitting={viewModel.form.submitStatus === 'submitting'}
            submitStatus={viewModel.form.submitStatusForBanner}
          />
        </Stack>
      </PageShell>
    </Box>
  );
};
